import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Spin, Card } from 'antd';
import { useTranslation } from 'react-i18next';

import EcAccountShow from '@components/shared/ec_accounts/EcAccountShow';
import { ecAccountService } from '@services/operator/ecAccountService';
import { handleApiError } from '@utils/apiErrorHandler';
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext';

export default function OperatorEcAccountShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addJob } = useOperatorNotification();
  const [ecAccount, setEcAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEcAccount = async () => {
      setLoading(true);
      try {
        const data = await ecAccountService.get(id);
        if (data.success) {
          setEcAccount(data.data);
        } else {
          handleApiError(data, { t, fallbackMessage: t('common.error.record_not_found') });
          navigate('/ec_accounts');
        }
      } catch (error) {
        console.error('Failed to load ec_account:', error);
        message.error(t('common.message.load_failed'));
      } finally {
        setLoading(false);
      }
    };
    loadEcAccount();
  }, [id]);

  const handleDelete = async () => {
    try {
      const result = await ecAccountService.delete(ecAccount.id);
      if (result.success) {
        message.success(t('common.message.delete_success'));
        navigate('/ec_accounts');
      } else {
        message.error(result.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      message.error(t('common.error.operation_failed'));
    }
  };

  const handleScheduleFetch = async (ecAccountId) => {
    const result = await ecAccountService.scheduleFetch(ecAccountId);
    if (!result.success) {
      handleApiError(result, { t, fallbackMessage: t('common.error.operation_failed') });
      throw new Error(result.message);
    }
    // 重新載入資料以更新 next_run_at
    const data = await ecAccountService.get(id);
    if (data.success) {
      setEcAccount(data.data);
    }
  };

  const handleSyncProductMappings = async (ecAccountId) => {
    const result = await ecAccountService.syncProductMappings(ecAccountId);
    if (!result.success) {
      handleApiError(result, { t, fallbackMessage: t('common.error.operation_failed') });
      throw new Error(result.message);
    }
    const { job_id } = result.data;
    if (addJob && job_id) {
      addJob(job_id, 'background_job.workers.sku_sync.start');
    }
    message.success(t('models.ec_account.sync_product_mappings_queued'));
  };

  const handleSyncStock = async (ecAccountId) => {
    const result = await ecAccountService.syncStock(ecAccountId);
    if (!result.success) {
      handleApiError(result, { t, fallbackMessage: t('common.error.operation_failed') });
      throw new Error(result.message);
    }
    message.success(t('models.ec_account.sync_stock_enqueued'));
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <EcAccountShow
      ecAccount={ecAccount}
      role="operator"
      onDelete={handleDelete}
      onScheduleFetch={handleScheduleFetch}
      onSyncProductMappings={handleSyncProductMappings}
      onSyncStock={handleSyncStock}
    />
  );
}
